import { useRouter } from 'next/router';
import React, { memo, useMemo } from 'react';

import { Grid } from '@sravni/react-design-system';
import type { ColSpan } from '@sravni/react-design-system/dist/types/components/Grid/types/Col.types';
import { useIsMobile } from '@sravni/react-utils';
import type { Seo } from '@sravni/types/lib/seo';

import type * as Microcredits from '@src/@types/microcredits';
import {
    PageHeaderBranding as PageHeaderBrandingBase,
    PageHeaderBrandingContainer,
    PageHeaderLocation,
    PageHeaderRegionsDialog,
    PageHeaderSponsor,
    PageHeaderSubHeading,
    SimplePageHeaderFilters,
    useHeadingTexts,
    useRegionDialogToggle,
} from '@src/components/PageHeader';
import { PageHeaderFilters } from '@src/components/PageHeader/PageHeaderFilters';
import { SimplePageHeaderPhone } from '@src/components/PageHeader/SimplePageHeaderPhone';
import { Experiments } from '@src/constants/abTest';
import { isNaKartuPage } from '@src/helpers/isNaKartuPage';
import { useCreditsExtraFiltersVisibility } from '@src/hooks/useCreditsExtraFiltersVisibility';
import { useTestVariant } from '@src/hooks/useTestVariants';
import { useDeviceBranding } from '@src/reducers/branding';
import { useHasUtmLabel } from '@src/reducers/route';
import { isSpecialPageWithoutQuery } from '@src/utils/routing';

import styles from './styles.module.scss';

interface IProps {
    filters: Microcredits.IFilters;
    difference?: number;
    onChange: (value: any, name: string) => void;
    seo?: Partial<Seo>;
}

export const PageHeaderBranding: React.FC<IProps> = memo(({ filters, difference, onChange, seo }) => {
    const [conditionsActive] = useCreditsExtraFiltersVisibility();
    const isMobile = useIsMobile();
    const { asPath } = useRouter();
    const { creditCalculator } = useDeviceBranding() || {};
    const { isOpenedDialog, closeRegionDialog, toggleRegionDialog } = useRegionDialogToggle();
    const { headingText, subHeadingText } = useHeadingTexts();
    const hasUtmLabel = useHasUtmLabel();
    const span = getSpan(asPath);
    const isSpecial = isSpecialPageWithoutQuery(asPath);

    const [pathWithoutQuery] = asPath.split('?');
    const showNewFilters = isNaKartuPage(pathWithoutQuery);

    const { isBVariant } = useTestVariant(Experiments.CHECKER_ON_MAIN_SEO_EXPERIMENT);

    const isPhoneInputShown = isSpecial || (pathWithoutQuery === '/zaimy/' && !hasUtmLabel && isBVariant);

    const FiltersElement = useMemo(() => {
        if (isPhoneInputShown) {
            return <SimplePageHeaderPhone />;
        }

        return showNewFilters ? (
            <PageHeaderFilters
                filters={filters}
                difference={difference}
                conditionsActive={conditionsActive}
                onChange={onChange}
                seo={seo}
            />
        ) : (
            <SimplePageHeaderFilters
                filters={filters}
                difference={difference}
                conditionsActive={conditionsActive}
                onChange={onChange}
                seo={seo}
            />
        );
    }, [isPhoneInputShown, showNewFilters, filters, difference, conditionsActive, onChange, seo]);

    if (isMobile) {
        return (
            <>
                {FiltersElement}
                <div className={styles.hidden_heading_section}>
                    <h1>{headingText}</h1>
                    <div>{subHeadingText}</div>
                </div>
            </>
        );
    }

    return (
        <PageHeaderBrandingBase className={styles.page_header_branding} brandingConfig={creditCalculator}>
            <PageHeaderBrandingContainer className={styles.page_header_branding_container}>
                {!isSpecial && (
                    <Grid.Row>
                        <Grid.Col span={isMobile ? span.gridSpanMobile : span.gridSpan}>
                            <PageHeaderLocation
                                className={styles.location}
                                brandingConfig={creditCalculator}
                                position="center"
                                header={headingText}
                                onClick={toggleRegionDialog}
                            />
                            {!hasUtmLabel && <PageHeaderSubHeading>{subHeadingText}</PageHeaderSubHeading>}
                        </Grid.Col>
                        {!isMobile && (
                            <Grid.Col span={span.gridSpanRest} className={styles.sponsor_container}>
                                <PageHeaderSponsor />
                            </Grid.Col>
                        )}
                    </Grid.Row>
                )}
                {isSpecial && (
                    <div className={styles.specialTitle}>
                        Займы онлайн от лучших МФО
                        <sup className={styles.star}>*</sup>
                    </div>
                )}
                {FiltersElement}
                {isMobile && <PageHeaderSponsor />}
            </PageHeaderBrandingContainer>
            <PageHeaderRegionsDialog isOpen={isOpenedDialog} onClose={closeRegionDialog} />
        </PageHeaderBrandingBase>
    );
});

interface ISpanObject {
    gridSpan: ColSpan;
    gridSpanRest: ColSpan;
    gridSpanMobile: ColSpan;
}

function getSpan(asPath: string): ISpanObject {
    if (asPath === '/zaimy/pod-0-protsentov/') {
        return {
            gridSpan: 7,
            gridSpanRest: 5,
            gridSpanMobile: 12,
        };
    }

    return {
        gridSpan: 6,
        gridSpanRest: 6,
        gridSpanMobile: 12,
    };
}
